<template>
  <div
    ref="container"
    class="floor-plan-canvas-container"
    @wheel="handleWheel"
  >
    <div ref="canvasWrapper" class="canvas-wrapper"></div>

    <!-- Tooltip for hovering over objects -->
    <v-card
      v-if="hoveredTable"
      class="table-tooltip elevation-4"
      :style="tooltipStyle"
    >
      <v-card-text class="pa-2">
        <div class="font-weight-bold">{{ hoveredTable.tableName }}</div>
        <div class="text-caption">
          <v-chip x-small :color="getStatusColor(hoveredTable.status)" dark class="mr-1">
            {{ getStatusText(hoveredTable.status) }}
          </v-chip>
        </div>
        <div v-if="hoveredTable.itemCount > 0" class="text-caption mt-1">
          {{ $t('$vuetify.floorPlan.items') }}: {{ hoveredTable.itemCount }}<br>
          {{ $t('$vuetify.floorPlan.total') }}: {{ formatCurrency(hoveredTable.totalAmount) }}<br>
          {{ $t('$vuetify.floorPlan.waiting') }}: {{ hoveredTable.getFormattedWaitingTime() }}
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Konva from 'konva'
import {
  TableObject,
  WallObject,
  DoorObject,
  BarObject,
  TextBoxObject,
  AreaObject,
  snapToGrid
} from './FloorPlanObjects'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'FloorPlanCanvas',
  props: {
    room: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    },
    activeTool: {
      type: String,
      default: 'select'
    },
    tables: {
      type: Array,
      default: () => []
    },
    zoom: {
      type: Number,
      default: 1
    },
    snapToGrid: {
      type: Boolean,
      default: true
    },
    showGrid: {
      type: Boolean,
      default: true
    },
    backgroundPattern: {
      type: String,
      default: 'none'
    },
    backgroundColor: {
      type: String,
      default: '#f5f5f5'
    },
    keyboardShortcutsEnabled: {
      type: Boolean,
      default: false
    },
    showShortcutBadges: {
      type: Boolean,
      default: false
    },
    showBadgesTemporarily: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      stage: null,
      layer: null,
      gridLayer: null,
      backgroundLayer: null,
      transformer: null,
      konvaObjects: new Map(), // Map of object id -> Konva shape
      selectedObject: null,
      hoveredTable: null,
      tooltipX: 0,
      tooltipY: 0,
      isDragging: false, // Flag to prevent re-render during drag
      drawingWall: null, // Wall being drawn
      drawingArea: null, // Area being drawn
      tempLine: null, // Temporary line for wall drawing
      tempWallPoints: [], // Temporary point markers for wall drawing
      keydownHandler: null, // Reference to keydown handler for cleanup
      keyupHandler: null, // Reference to keyup handler for cleanup
      areaStartPos: null, // Start position for area drawing
      tempAreaRect: null, // Temporary rectangle for area drawing
      isShiftPressed: false, // Track shift key for multi-select
      timerCount: 0, // Timer for updating table waiting times
      updateTimer: null, // Interval for timer
      resizeHandles: [], // Custom resize handles
      objectShortcutMap: {}, // Map shortcuts to objects (tables/bars)
      shortcutKeyBuffer: '', // Buffer for multi-character shortcuts
      shortcutKeyTimer: null // Timer for shortcut input
    }
  },
  computed: {
    gridSize () {
      return 20
    },
    tooltipStyle () {
      return {
        position: 'fixed',
        left: `${this.tooltipX}px`,
        top: `${this.tooltipY}px`,
        pointerEvents: 'none',
        zIndex: 1000
      }
    }
  },
  watch: {
    zoom (newZoom) {
      if (this.stage) {
        this.stage.scale({ x: newZoom, y: newZoom })
        this.stage.batchDraw()
      }
    },
    showGrid (show) {
      if (this.gridLayer) {
        this.gridLayer.visible(show)
        this.gridLayer.batchDraw()
      }
    },
    room: {
      deep: true,
      handler () {
        // Don't re-render if currently dragging
        if (this.isDragging) return

        this.renderObjects()
        this.$nextTick(() => {
          this.assignObjectShortcuts()
        })
      }
    },
    showShortcutBadges () {
      // Re-render when badge visibility changes
      this.renderObjects()
    },
    showBadgesTemporarily () {
      // Re-render when temporary badges visibility changes
      this.renderObjects()
    },
    tables: {
      deep: true,
      handler () {
        this.updateTableStatuses()
        if (!this.editMode) {
          this.startTimer()
        }
      }
    },
    activeTool (newTool) {
      // Clear selection when changing tools
      if (this.transformer) {
        this.transformer.nodes([])
        this.transformer.getLayer().batchDraw()
      }
      this.selectedObject = null

      // Emit deselection event
      this.$emit('object-selected', null)

      // Clean up any active drawing
      if (this.drawingWall) {
        this.cleanupWallDrawing()
      }

      // Disable stage dragging when drawing tools are active
      if (this.stage) {
        // Only allow stage dragging in select mode
        this.stage.draggable(this.editMode && newTool === 'select')
      }

      // Change cursor based on tool
      this.updateCursor(newTool)
    },
    editMode (isEdit) {
      // Enable/disable stage draggability based on mode and active tool
      if (this.stage) {
        // Only allow dragging in edit mode with select tool
        this.stage.draggable(isEdit && this.activeTool === 'select')
      }

      // Enable/disable interactions based on mode
      if (this.layer) {
        this.layer.children.forEach(shape => {
          if (isEdit) {
            shape.draggable(shape.attrs.objectType === 'table')
          } else {
            shape.draggable(false)
          }
        })
      }

      // Update grid visibility
      this.drawGrid()

      // Start/stop timer based on mode
      if (isEdit) {
        this.stopTimer()
      } else {
        this.renderObjects() // Re-render with user mode visuals
        this.startTimer()
      }
    },
    backgroundPattern () {
      this.updateBackgroundStyle()
    },
    backgroundColor () {
      this.updateBackgroundStyle()
    }
  },
  mounted () {
    this.initKonva()

    // Assign shortcuts before rendering so TableObject instances get the shortcut
    this.assignObjectShortcuts()

    this.renderObjects()
    this.updateBackgroundStyle()

    // Start timer if in user mode
    if (!this.editMode && this.tables.length > 0) {
      this.startTimer()
    }

    // Register keyboard listener for object shortcuts
    document.addEventListener('keydown', this.handleObjectKeyPress)
  },
  beforeDestroy () {
    this.stopTimer()

    // Clean up keyboard listeners
    document.removeEventListener('keydown', this.handleObjectKeyPress)
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler)
    }
    if (this.keyupHandler) {
      window.removeEventListener('keyup', this.keyupHandler)
    }
    if (this.shortcutKeyTimer) {
      clearTimeout(this.shortcutKeyTimer)
    }

    if (this.stage) {
      this.stage.destroy()
    }
  },
  methods: {
    /**
     * Darken a color by a percentage
     */
    darkenColor (color, percent) {
      // Convert hex to RGB
      let r, g, b
      if (color.startsWith('#')) {
        const hex = color.replace('#', '')
        r = parseInt(hex.substring(0, 2), 16)
        g = parseInt(hex.substring(2, 4), 16)
        b = parseInt(hex.substring(4, 6), 16)
      } else {
        return color
      }

      // Darken
      r = Math.floor(r * (1 - percent))
      g = Math.floor(g * (1 - percent))
      b = Math.floor(b * (1 - percent))

      // Convert back to hex
      return '#' + [r, g, b].map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }).join('')
    },

    /**
     * Convert Croatian characters to English equivalents
     */
    normalizeCharacters (text) {
      if (!text) return text
      const charMap = {
        š: 's',
        Š: 'S',
        č: 'c',
        Č: 'C',
        ć: 'c',
        Ć: 'C',
        ž: 'z',
        Ž: 'Z',
        đ: 'd',
        Đ: 'D'
      }
      return text.replace(/[šŠčČćĆžŽđĐ]/g, char => charMap[char] || char)
    },

    /**
     * Generate shortcut from label (first letters of first 2 words)
     */
    generateShortcut (label) {
      if (!label) return 'A'

      // First normalize Croatian characters to English
      const normalizedLabel = this.normalizeCharacters(label)

      const words = normalizedLabel.trim().split(/\s+/)
      let shortcut = words.slice(0, 2).map(w => w[0]).filter(Boolean).join('').toUpperCase()

      if (!shortcut) shortcut = 'A' // Fallback to 'A' if empty

      // Protected keywords (reserved for buttons)
      const protectedKeywords = ['F', 'T', 'S', 'O', 'G', 'K']

      // If shortcut starts with a number or is a protected keyword, add 'X' prefix
      if (/^\d/.test(shortcut) || protectedKeywords.includes(shortcut)) {
        shortcut = 'X' + shortcut
      }

      return shortcut
    },

    /**
     * Check if a table/bar center point is inside an area
     */
    isTableInArea (table, area) {
      // Calculate center of table
      const tableCenterX = table.x + table.width / 2
      const tableCenterY = table.y + table.height / 2

      // Check if center is inside area rectangle
      return (
        tableCenterX >= area.x &&
        tableCenterX <= area.x + area.width &&
        tableCenterY >= area.y &&
        tableCenterY <= area.y + area.height
      )
    },

    /**
     * Assign shortcuts to areas, tables and bars in current room
     */
    assignObjectShortcuts () {
      this.objectShortcutMap = {}
      const usedShortcuts = new Set()
      let nextAutoNumber = 100

      if (!this.room || !this.room.objects) return

      // First pass: Assign letter shortcuts to areas from first letter of label
      const areas = this.room.objects.filter(obj => obj.type === 'area')
      const areaShortcutCount = {}

      areas.forEach((area) => {
        const label = area.label || 'Area'
        const normalizedLabel = this.normalizeCharacters(label)
        let letter = normalizedLabel[0].toUpperCase()

        // Handle duplicates - add number suffix
        if (areaShortcutCount[letter] !== undefined) {
          areaShortcutCount[letter]++
          letter = letter + areaShortcutCount[letter]
        } else {
          areaShortcutCount[letter] = 0
        }

        area.shortcut = letter
        usedShortcuts.add(letter)
      })

      // Second pass: Assign number shortcuts to tables and bars
      this.room.objects.forEach(objData => {
        if (objData.type === 'table' || objData.type === 'bar') {
          const label = objData.tableName || objData.barName || 'Object'

          // Extract first 3 digits from label
          const match = label.match(/\d{1,3}/)
          let number = match ? match[0] : null

          // If no number found, assign new number starting from 100
          if (!number) {
            number = String(nextAutoNumber)
            nextAutoNumber++
          }

          // Handle duplicates - add X prefix
          if (usedShortcuts.has(number)) {
            number = 'X' + number
          }

          // Find which area this table belongs to (for display purposes)
          let areaShortcut = ''
          for (const area of areas) {
            if (this.isTableInArea(objData, area)) {
              areaShortcut = area.shortcut
              break
            }
          }

          // Store only the number as shortcut (for keyboard binding)
          objData.shortcut = number
          // Store combined area+number for display on cash register
          objData.displayShortcut = areaShortcut + number

          usedShortcuts.add(number)
          this.objectShortcutMap[number] = objData
        }
      })
    },

    /**
     * Handle keyboard shortcut press for selecting objects
     */
    handleObjectKeyPress (e) {
      // Ignore if keyboard shortcuts are disabled
      if (!this.keyboardShortcutsEnabled) return

      // Ignore if in edit mode
      if (this.editMode) return

      // Ignore if user is typing in an input field
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return

      const key = e.key

      // Handle letters for object selection
      if (/^[a-zA-Z0-9]$/.test(key)) {
        // Clear previous timer
        if (this.shortcutKeyTimer) {
          clearTimeout(this.shortcutKeyTimer)
        }

        // Add key to buffer (uppercase for letters)
        this.shortcutKeyBuffer += key.toUpperCase()

        // Set timer to process buffer after 500ms
        this.shortcutKeyTimer = setTimeout(() => {
          const buffer = this.shortcutKeyBuffer

          // Try to match object shortcut (letters for areas, numbers for tables/bars)
          const objData = this.objectShortcutMap[buffer]
          if (objData) {
            // Click the object (table or bar)
            if (objData.type === 'table' && objData.tableId) {
              this.$emit('table-clicked', objData.tableId)
            }
          }

          // Clear buffer
          this.shortcutKeyBuffer = ''
        }, 500)
      }
    },

    /**
     * Initialize Konva stage and layers
     */
    initKonva () {
      const container = this.$refs.canvasWrapper
      const width = container.clientWidth
      const height = container.clientHeight

      // Create stage
      this.stage = new Konva.Stage({
        container: this.$refs.canvasWrapper,
        width: width,
        height: height,
        // Only allow dragging in edit mode with select tool
        draggable: this.editMode && this.activeTool === 'select'
      })

      // Create background layer (bottom-most)
      this.backgroundLayer = new Konva.Layer({
        listening: false // Don't intercept events - let them pass through
      })
      this.stage.add(this.backgroundLayer)

      // Create grid layer (above background)
      this.gridLayer = new Konva.Layer()
      this.stage.add(this.gridLayer)
      this.drawGrid()

      // Create main layer
      this.layer = new Konva.Layer()
      this.stage.add(this.layer)

      // Create transformer for edit mode
      this.transformer = new Konva.Transformer({
        rotateEnabled: true,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'middle-left', 'middle-right', 'bottom-center'],
        borderStroke: '#0066cc',
        borderStrokeWidth: 2,
        anchorFill: '#0066cc',
        anchorStroke: '#ffffff',
        anchorSize: 10,
        anchorStrokeWidth: 2,
        anchorCornerRadius: 2,
        rotateAnchorOffset: 30,
        padding: 5,
        visible: true,
        boundBoxFunc: (oldBox, newBox) => {
          // Limit minimum size
          if (newBox.width < 5 || newBox.height < 5) {
            return oldBox
          }
          return newBox
        }
      })
      this.layer.add(this.transformer)

      // Setup event listeners
      this.setupEventListeners()

      // Set initial zoom
      this.stage.scale({ x: this.zoom, y: this.zoom })

      // Setup keyboard shortcuts
      this.setupKeyboardShortcuts()
    },

    /**
     * Draw grid on grid layer
     */
    drawGrid () {
      if (!this.gridLayer) return

      this.gridLayer.destroyChildren()

      const width = this.room.width || 1200
      const height = this.room.height || 800
      const gridSize = this.gridSize

      // Vertical lines
      for (let x = 0; x <= width; x += gridSize) {
        const line = new Konva.Line({
          points: [x, 0, x, height],
          stroke: '#e0e0e0',
          strokeWidth: 1
        })
        this.gridLayer.add(line)
      }

      // Horizontal lines
      for (let y = 0; y <= height; y += gridSize) {
        const line = new Konva.Line({
          points: [0, y, width, y],
          stroke: '#e0e0e0',
          strokeWidth: 1
        })
        this.gridLayer.add(line)
      }

      // Grid is only visible in edit mode
      this.gridLayer.visible(this.showGrid && this.editMode)
      this.gridLayer.batchDraw()
    },

    /**
     * Setup event listeners for interactions
     */
    setupEventListeners () {
      // Click on stage background (both mouse and touch)
      const handleStageClickOrTap = (e) => {
        // Allow clicks on stage or temporary drawing objects (tempLine, tempWallPoints)
        const isStage = e.target === this.stage
        const isTempObject = e.target === this.tempLine ||
                           this.tempWallPoints.some(marker => marker === e.target)

        if (isStage || (this.drawingWall && isTempObject)) {
          this.handleStageClick(e)
        }
      }

      this.stage.on('click', handleStageClickOrTap)
      this.stage.on('tap', handleStageClickOrTap)

      // Double click to finish drawing (both mouse and touch)
      const handleDoubleClickOrTap = (e) => {
        if (this.drawingWall) {
          this.finishDrawingWall()
        }
      }

      this.stage.on('dblclick', handleDoubleClickOrTap)
      this.stage.on('dbltap', handleDoubleClickOrTap)

      // Mouse move for drawing and tooltips (both mouse and touch)
      const handleMove = (e) => {
        this.handleMouseMove(e)
      }

      this.stage.on('mousemove', handleMove)
      this.stage.on('touchmove', handleMove)

      // Mouse/touch leave - hide tooltip
      const handleLeave = () => {
        this.hoveredTable = null
      }

      this.stage.on('mouseleave', handleLeave)
      this.stage.on('touchend', handleLeave)

      // Mouse/touch down for drag drawing
      const handleDown = (e) => {
        if (e.target !== this.stage) return
        this.handleMouseDown(e)
      }

      this.stage.on('mousedown', handleDown)
      this.stage.on('touchstart', handleDown)

      // Mouse/touch up for ending drag drawing
      const handleUp = (e) => {
        this.handleMouseUp(e)
      }

      this.stage.on('mouseup', handleUp)
      this.stage.on('touchend', handleUp)
    },

    /**
     * Setup keyboard shortcuts
     */
    setupKeyboardShortcuts () {
      this.keydownHandler = (e) => {
        if (e.key === 'Shift') {
          this.isShiftPressed = true
        }

        if (!this.editMode) return

        // Delete selected object
        if (e.key === 'Delete' && this.selectedObject) {
          this.deleteSelectedObject()
        }

        // Ctrl+D / Cmd+D — duplicate selected object
        if ((e.ctrlKey || e.metaKey) && e.key === 'd' && this.selectedObject) {
          e.preventDefault()
          this.duplicateSelectedObject()
        }

        // Escape - finish drawing or deselect
        if (e.key === 'Escape') {
          // If drawing wall, finish it
          if (this.drawingWall) {
            this.finishDrawingWall()
          } else {
            // Otherwise deselect
            if (this.selectedObject) {
              const prevShape = this.konvaObjects.get(this.selectedObject.id)
              if (prevShape) {
                this.removeSelectionHighlight(prevShape)
              }
            }

            // Remove resize handles
            this.removeResizeHandles()

            if (this.transformer) {
              this.transformer.nodes([])
              const layer = this.transformer.getLayer()
              if (layer) {
                layer.batchDraw()
              }
            }
            this.selectedObject = null

            // Emit deselection event
            this.$emit('object-selected', null)
          }
        }
      }

      this.keyupHandler = (e) => {
        if (e.key === 'Shift') {
          this.isShiftPressed = false
        }
      }

      window.addEventListener('keydown', this.keydownHandler)
      window.addEventListener('keyup', this.keyupHandler)
    },

    /**
     * Handle mousedown for drag drawing
     */
    handleMouseDown (e) {
      if (!this.editMode) return

      const pos = this.stage.getPointerPosition()
      const stageScale = this.stage.scaleX()
      const stagePos = this.stage.position()

      const x = (pos.x - stagePos.x) / stageScale
      const y = (pos.y - stagePos.y) / stageScale

      if (this.activeTool === 'add-area') {
        // Start drawing area
        this.areaStartPos = {
          x: this.snapToGrid ? snapToGrid(x, this.gridSize) : x,
          y: this.snapToGrid ? snapToGrid(y, this.gridSize) : y
        }
      }
    },

    /**
     * Handle mouseup for ending drag drawing
     */
    handleMouseUp (e) {
      if (!this.editMode) return

      if (this.activeTool === 'add-area' && this.areaStartPos) {
        this.finishDrawingArea()
      }
    },

    /**
     * Handle click on stage background
     */
    handleStageClick (e) {
      const pos = this.stage.getPointerPosition()
      const stageScale = this.stage.scaleX()
      const stagePos = this.stage.position()

      // Calculate position relative to stage
      const x = (pos.x - stagePos.x) / stageScale
      const y = (pos.y - stagePos.y) / stageScale

      if (this.editMode) {
        this.handleEditModeClick(x, y)
        // Don't deselect while drawing tools are active
        if (this.activeTool !== 'add-wall') {
          // Deselect and remove highlight
          if (this.selectedObject) {
            const prevShape = this.konvaObjects.get(this.selectedObject.id)
            if (prevShape) {
              this.removeSelectionHighlight(prevShape)
            }
          }

          // Remove resize handles
          this.removeResizeHandles()

          if (this.transformer) {
            this.transformer.nodes([])
            this.transformer.getLayer().batchDraw()
          }
          this.selectedObject = null

          // Emit deselection event
          this.$emit('object-selected', null)
        }
      } else {
        // In user mode, always handle deselection
        if (this.selectedObject) {
          const prevShape = this.konvaObjects.get(this.selectedObject.id)
          if (prevShape) {
            this.removeSelectionHighlight(prevShape)
          }
        }

        // Remove resize handles
        this.removeResizeHandles()

        if (this.transformer) {
          this.transformer.nodes([])
          this.transformer.getLayer().batchDraw()
        }
        this.selectedObject = null

        // Emit deselection event
        this.$emit('object-selected', null)
      }
    },

    /**
     * Handle click in edit mode
     */
    handleEditModeClick (x, y) {
      const snappedX = this.snapToGrid ? snapToGrid(x, this.gridSize) : x
      const snappedY = this.snapToGrid ? snapToGrid(y, this.gridSize) : y

      switch (this.activeTool) {
        case 'add-table-circle':
        case 'add-table-square':
        case 'add-table-rectangle':
        case 'add-table-oval':
        case 'add-table-corner':
          this.addTable(snappedX, snappedY)
          break

        case 'add-wall':
          this.addWallPoint(snappedX, snappedY)
          break

        case 'add-door':
          this.addDoor(snappedX, snappedY)
          break

        case 'add-bar-rectangle':
          this.addBar(snappedX, snappedY, 'rectangle')
          break

        case 'add-bar-semicircle':
          this.addBar(snappedX, snappedY, 'semicircle')
          break

        case 'add-bar-semioval':
          this.addBar(snappedX, snappedY, 'semioval')
          break

        case 'add-area':
          // Area is drawn with drag, not click
          break

        case 'add-textbox':
          this.addTextBox(snappedX, snappedY)
          break
      }
    },

    /**
     * Add table at position
     */
    addTable (x, y) {
      const shape = this.activeTool.replace('add-table-', '')

      // Set default dimensions based on shape
      let width, height
      if (shape === 'rectangle') {
        width = 240
        height = 120
      } else if (shape === 'oval') {
        width = 240
        height = 120
      } else {
        width = 120 // Circle, square, corner
        height = 120
      }

      const table = new TableObject({
        id: uuidv4(),
        x: x,
        y: y,
        width: width,
        height: height,
        shape: shape,
        tableName: `Stol ${this.room.objects.filter(o => o.type === 'table').length + 1}`,
        tableId: null, // Will be linked later
        seats: 4
      })

      this.$emit('object-added', table.toJSON())
      this.drawTable(table)

      // Select the newly added table and switch to select tool
      this.$nextTick(() => {
        const konvaObj = this.konvaObjects.get(table.id)
        if (konvaObj) {
          this.selectObject(konvaObj)
        }
        // Switch to select tool
        this.$emit('tool-changed', 'select')
      })
    },

    /**
     * Add door at position
     */
    addDoor (x, y) {
      const door = new DoorObject({
        id: uuidv4(),
        x: x,
        y: y,
        width: 80,
        height: 10,
        doorSwing: 'left-in'
      })

      this.$emit('object-added', door.toJSON())
      this.drawDoor(door)

      // Select the newly added door and switch to select tool
      this.$nextTick(() => {
        const konvaObj = this.konvaObjects.get(door.id)
        if (konvaObj) {
          this.selectObject(konvaObj)
        }
        // Switch to select tool
        this.$emit('tool-changed', 'select')
      })
    },

    /**
     * Add bar at position
     */
    addBar (x, y, shape = 'rectangle') {
      const bar = new BarObject({
        id: uuidv4(),
        x: x,
        y: y,
        width: 150,
        height: 100,
        shape: shape
      })

      this.$emit('object-added', bar.toJSON())
      this.drawBar(bar)

      // Select the newly added bar and switch to select tool
      this.$nextTick(() => {
        const konvaObj = this.konvaObjects.get(bar.id)
        if (konvaObj) {
          this.selectObject(konvaObj)
        }
        this.$emit('tool-changed', 'select')
      })
    },

    /**
     * Add text box at position
     */
    addTextBox (x, y) {
      const textBox = new TextBoxObject({
        id: uuidv4(),
        x: x,
        y: y,
        width: 200,
        height: 60,
        text: 'Text'
      })

      this.$emit('object-added', textBox.toJSON())
      this.drawTextBox(textBox)

      // Select the newly added text box and switch to select tool
      this.$nextTick(() => {
        const konvaObj = this.konvaObjects.get(textBox.id)
        if (konvaObj) {
          this.selectObject(konvaObj)
        }
        this.$emit('tool-changed', 'select')
      })
    },

    /**
     * Add point to wall being drawn
     */
    addWallPoint (x, y) {
      if (!this.drawingWall) {
        // Start new wall
        this.drawingWall = new WallObject({
          id: uuidv4(),
          points: [{ x, y }]
        })
      } else {
        // Add point to existing wall
        this.drawingWall.addPoint(x, y)
      }

      // Add visual marker for this point
      const pointMarker = new Konva.Circle({
        x: x,
        y: y,
        radius: 5,
        fill: '#FF5722',
        stroke: '#FFFFFF',
        strokeWidth: 2,
        listening: false // Don't intercept mouse events
      })
      this.tempWallPoints.push(pointMarker)
      this.layer.add(pointMarker)

      // Update the temporary line
      this.updateTempWallLine()
    },

    /**
     * Update temporary wall line during drawing
     */
    updateTempWallLine () {
      if (!this.drawingWall) return

      if (this.tempLine) {
        this.tempLine.destroy()
      }

      const points = []
      this.drawingWall.points.forEach(p => {
        points.push(p.x, p.y)
      })

      this.tempLine = new Konva.Line({
        points: points,
        stroke: this.drawingWall.color,
        strokeWidth: this.drawingWall.thickness,
        lineCap: 'round',
        lineJoin: 'round',
        dash: [10, 5],
        listening: false // Don't intercept mouse events
      })

      this.layer.add(this.tempLine)
      this.layer.batchDraw()
    },

    /**
     * Finish drawing wall
     */
    finishDrawingWall () {
      if (!this.drawingWall || !this.drawingWall.isValid()) {
        // Clean up even if invalid
        this.cleanupWallDrawing()
        return
      }

      if (this.tempLine) {
        this.tempLine.destroy()
        this.tempLine = null
      }

      // Remove point markers
      this.tempWallPoints.forEach(marker => marker.destroy())
      this.tempWallPoints = []

      this.$emit('object-added', this.drawingWall.toJSON())
      this.drawWall(this.drawingWall)

      this.drawingWall = null
      this.layer.batchDraw()
    },

    /**
     * Finish drawing area
     */
    finishDrawingArea () {
      if (!this.areaStartPos) return

      if (this.tempAreaRect) {
        const width = this.tempAreaRect.width()
        const height = this.tempAreaRect.height()

        if (width > 20 && height > 20) {
          const area = new AreaObject({
            id: uuidv4(),
            x: this.tempAreaRect.x(),
            y: this.tempAreaRect.y(),
            width: width,
            height: height,
            label: `Area ${this.room.objects.filter(o => o.type === 'area').length + 1}`
          })

          this.$emit('object-added', area.toJSON())
          this.drawArea(area)
        }

        this.tempAreaRect.destroy()
        this.tempAreaRect = null
      }

      this.areaStartPos = null
      this.layer.batchDraw()
    },

    /**
     * Clean up wall drawing temporary objects
     */
    cleanupWallDrawing () {
      if (this.tempLine) {
        this.tempLine.destroy()
        this.tempLine = null
      }

      this.tempWallPoints.forEach(marker => marker.destroy())
      this.tempWallPoints = []

      this.drawingWall = null
      this.layer.batchDraw()
    },

    /**
     * Delete selected object
     */
    deleteSelectedObject () {
      if (!this.selectedObject) return

      // Remove resize handles
      this.removeResizeHandles()

      // Clear transformer FIRST before destroying the object
      if (this.transformer) {
        this.transformer.nodes([])
        this.transformer.getLayer().batchDraw()
      }

      const konvaObj = this.konvaObjects.get(this.selectedObject.id)
      if (konvaObj) {
        // Remove selection highlight before destroying
        this.removeSelectionHighlight(konvaObj)

        konvaObj.destroy()
        this.konvaObjects.delete(this.selectedObject.id)
        this.layer.batchDraw()
      }

      this.$emit('object-deleted', this.selectedObject.id)

      this.selectedObject = null

      // Emit deselection event
      this.$emit('object-selected', null)
    },

    duplicateSelectedObject () {
      if (!this.selectedObject) return

      const json = this.selectedObject.toJSON ? this.selectedObject.toJSON() : { ...this.selectedObject }
      const copy = {
        ...json,
        id: uuidv4(),
        x: (json.x || 0) + 30,
        y: (json.y || 0) + 30,
        tableId: null
      }

      if (copy.type === 'table' || copy.type === 'bar') {
        copy.tableName = (copy.tableName || '') + ' (kopija)'
      }

      let newObj
      switch (copy.type) {
        case 'table': newObj = new TableObject(copy); this.drawTable(newObj); break
        case 'wall': newObj = new WallObject(copy); this.drawWall(newObj); break
        case 'door': newObj = new DoorObject(copy); this.drawDoor(newObj); break
        case 'bar': newObj = new BarObject(copy); this.drawBar(newObj); break
        case 'textbox': newObj = new TextBoxObject(copy); this.drawTextBox(newObj); break
        case 'area': newObj = new AreaObject(copy); this.drawArea(newObj); break
        default: return
      }

      this.$emit('object-added', copy)

      this.$nextTick(() => {
        const konvaObj = this.konvaObjects.get(copy.id)
        if (konvaObj) this.selectObject(konvaObj)
      })
    },

    /**
     * Handle mouse move
     */
    handleMouseMove (e) {
      const pos = this.stage.getPointerPosition()

      // Update tooltip position
      this.tooltipX = pos.x + 15
      this.tooltipY = pos.y + 15

      // Check if hovering over a table
      const shape = e.target
      if (shape && shape.attrs.objectType === 'table' && shape.attrs.objectData) {
        this.hoveredTable = shape.attrs.objectData
      } else {
        this.hoveredTable = null
      }

      // Update temp wall line during drawing
      if (this.drawingWall && this.drawingWall.points.length > 0) {
        const stageScale = this.stage.scaleX()
        const stagePos = this.stage.position()
        const x = (pos.x - stagePos.x) / stageScale
        const y = (pos.y - stagePos.y) / stageScale

        if (this.tempLine) {
          const points = []
          this.drawingWall.points.forEach(p => {
            points.push(p.x, p.y)
          })
          points.push(x, y)
          this.tempLine.points(points)
          this.layer.batchDraw()
        }
      }

      // Update temp area rectangle during drawing
      if (this.areaStartPos && this.activeTool === 'add-area') {
        const stageScale = this.stage.scaleX()
        const stagePos = this.stage.position()
        const x = (pos.x - stagePos.x) / stageScale
        const y = (pos.y - stagePos.y) / stageScale

        const snappedX = this.snapToGrid ? snapToGrid(x, this.gridSize) : x
        const snappedY = this.snapToGrid ? snapToGrid(y, this.gridSize) : y

        if (this.tempAreaRect) {
          this.tempAreaRect.destroy()
        }

        const width = snappedX - this.areaStartPos.x
        const height = snappedY - this.areaStartPos.y

        if (Math.abs(width) > 10 && Math.abs(height) > 10) {
          this.tempAreaRect = new Konva.Rect({
            x: width > 0 ? this.areaStartPos.x : snappedX,
            y: height > 0 ? this.areaStartPos.y : snappedY,
            width: Math.abs(width),
            height: Math.abs(height),
            fill: '#f0f0f0',
            stroke: '#cccccc',
            strokeWidth: 2,
            opacity: 0.5,
            dash: [10, 5]
          })

          this.layer.add(this.tempAreaRect)
          this.layer.batchDraw()
        }
      }
    },

    /**
     * Handle mouse wheel for zooming
     */
    handleWheel (e) {
      // Disable zoom in user mode
      if (!this.editMode) return

      e.preventDefault()

      const scaleBy = 1.1
      const stage = this.stage
      const oldScale = stage.scaleX()
      const pointer = stage.getPointerPosition()

      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale
      }

      const newScale = e.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy

      // Limit zoom
      const limitedScale = Math.max(0.3, Math.min(3, newScale))

      stage.scale({ x: limitedScale, y: limitedScale })

      const newPos = {
        x: pointer.x - mousePointTo.x * limitedScale,
        y: pointer.y - mousePointTo.y * limitedScale
      }
      stage.position(newPos)
      stage.batchDraw()

      this.$emit('zoom-changed', limitedScale)
    },

    /**
     * Render all objects from room data
     */
    renderObjects () {
      if (!this.layer) return

      // Clear existing objects
      this.layer.destroyChildren()
      this.konvaObjects.clear()

      // Re-add transformer
      this.layer.add(this.transformer)

      // Draw all objects
      if (this.room && this.room.objects) {
        this.room.objects.forEach(objData => {
          switch (objData.type) {
            case 'table':
              this.drawTable(new TableObject(objData))
              break
            case 'wall':
              this.drawWall(new WallObject(objData))
              break
            case 'door':
              this.drawDoor(new DoorObject(objData))
              break
            case 'bar':
              this.drawBar(new BarObject(objData))
              break
            case 'textbox':
              this.drawTextBox(new TextBoxObject(objData))
              break
            case 'area':
              this.drawArea(new AreaObject(objData))
              break
          }
        })
      }

      this.layer.batchDraw()
    },

    /**
     * Add chairs to corner table
     */
    addChairsToCornerTable (group, table, chairSize, chairDistance, chairColor, chairStrokeColor) {
      const seats = table.seats || 4
      const size = Math.min(table.width, table.height)
      const angle = table.cornerAngle || 0

      // Place chairs along the arc
      for (let i = 0; i < seats; i++) {
        const angleStep = (Math.PI / 2) / (seats + 1)
        const chairAngle = angleStep * (i + 1)

        let x, y

        switch (angle) {
          case 0: // Top-left
            x = Math.cos(chairAngle) * (size + chairDistance)
            y = Math.sin(chairAngle) * (size + chairDistance)
            break
          case 90: // Top-right
            x = size - Math.sin(chairAngle) * (size + chairDistance)
            y = Math.cos(chairAngle) * (size + chairDistance)
            break
          case 180: // Bottom-right
            x = size - Math.cos(chairAngle) * (size + chairDistance)
            y = size - Math.sin(chairAngle) * (size + chairDistance)
            break
          case 270: // Bottom-left
            x = Math.sin(chairAngle) * (size + chairDistance)
            y = size - Math.cos(chairAngle) * (size + chairDistance)
            break
          default:
            x = Math.cos(chairAngle) * (size + chairDistance)
            y = Math.sin(chairAngle) * (size + chairDistance)
        }

        const chair = new Konva.Rect({
          x: x - chairSize / 2,
          y: y - chairSize / 2,
          width: chairSize,
          height: chairSize,
          fill: chairColor,
          stroke: chairStrokeColor,
          strokeWidth: 1,
          cornerRadius: 2,
          listening: false
        })

        group.add(chair)
      }
    },

    /**
     * Add chairs around a table
     */
    addChairsToTable (group, table) {
      const seats = table.seats || 4
      const chairSize = 12
      const chairDistance = 8 // Distance from table edge
      const isCorner = table.shape === 'corner'
      const chairColor = table.chairColor || '#8B4513'
      const chairStrokeColor = this.darkenColor(chairColor, 0.3)

      // Corner tables have special chair placement
      if (isCorner) {
        this.addChairsToCornerTable(group, table, chairSize, chairDistance, chairColor, chairStrokeColor)
        return
      }

      // Calculate how many chairs on each side
      let distribution
      if (seats === 1) {
        distribution = { top: 1, right: 0, bottom: 0, left: 0 }
      } else if (seats === 2) {
        distribution = { top: 1, right: 0, bottom: 1, left: 0 }
      } else if (seats === 3) {
        distribution = { top: 1, right: 1, bottom: 1, left: 0 }
      } else if (seats === 4) {
        distribution = { top: 1, right: 1, bottom: 1, left: 1 }
      } else if (seats === 5) {
        distribution = { top: 2, right: 1, bottom: 1, left: 1 }
      } else if (seats === 6) {
        distribution = { top: 2, right: 1, bottom: 2, left: 1 }
      } else if (seats === 7) {
        distribution = { top: 2, right: 2, bottom: 2, left: 1 }
      } else if (seats === 8) {
        distribution = { top: 2, right: 2, bottom: 2, left: 2 }
      } else {
        // For more than 8, distribute evenly
        const perSide = Math.ceil(seats / 4)
        distribution = { top: perSide, right: perSide, bottom: perSide, left: perSide }
      }

      const width = table.width
      const height = table.height

      // With offset, center is at (0, 0)
      // Top chairs
      if (distribution.top > 0) {
        const spacing = width / (distribution.top + 1)
        for (let i = 0; i < distribution.top; i++) {
          const chair = new Konva.Rect({
            x: -width / 2 + spacing * (i + 1) - chairSize / 2,
            y: -height / 2 - chairDistance - chairSize,
            width: chairSize,
            height: chairSize,
            fill: chairColor,
            stroke: chairStrokeColor,
            strokeWidth: 1,
            cornerRadius: 2,
            listening: false
          })
          group.add(chair)
        }
      }

      // Bottom chairs
      if (distribution.bottom > 0) {
        const spacing = width / (distribution.bottom + 1)
        for (let i = 0; i < distribution.bottom; i++) {
          const chair = new Konva.Rect({
            x: -width / 2 + spacing * (i + 1) - chairSize / 2,
            y: height / 2 + chairDistance,
            width: chairSize,
            height: chairSize,
            fill: chairColor,
            stroke: chairStrokeColor,
            strokeWidth: 1,
            cornerRadius: 2,
            listening: false
          })
          group.add(chair)
        }
      }

      // Left chairs
      if (distribution.left > 0) {
        const spacing = height / (distribution.left + 1)
        for (let i = 0; i < distribution.left; i++) {
          const chair = new Konva.Rect({
            x: -width / 2 - chairDistance - chairSize,
            y: -height / 2 + spacing * (i + 1) - chairSize / 2,
            width: chairSize,
            height: chairSize,
            fill: chairColor,
            stroke: chairStrokeColor,
            strokeWidth: 1,
            cornerRadius: 2,
            listening: false
          })
          group.add(chair)
        }
      }

      // Right chairs
      if (distribution.right > 0) {
        const spacing = height / (distribution.right + 1)
        for (let i = 0; i < distribution.right; i++) {
          const chair = new Konva.Rect({
            x: width / 2 + chairDistance,
            y: -height / 2 + spacing * (i + 1) - chairSize / 2,
            width: chairSize,
            height: chairSize,
            fill: chairColor,
            stroke: chairStrokeColor,
            strokeWidth: 1,
            cornerRadius: 2,
            listening: false
          })
          group.add(chair)
        }
      }
    },

    /**
     * Create corner table shape (quarter circle)
     */
    createCornerShape (width, height, color, angle) {
      // Create a quarter circle using arc and lines
      const size = Math.min(width, height)

      // Create shape using line and arc (centered with offset)
      const halfSize = size / 2
      const shape = new Konva.Shape({
        sceneFunc: (context, shape) => {
          context.beginPath()

          switch (angle) {
            case 0: // Top-left
              context.moveTo(-halfSize, halfSize)
              context.lineTo(-halfSize, -halfSize)
              context.lineTo(halfSize, -halfSize)
              context.arc(-halfSize, -halfSize, size, 0, Math.PI / 2)
              break
            case 90: // Top-right
              context.moveTo(-halfSize, -halfSize)
              context.lineTo(halfSize, -halfSize)
              context.lineTo(halfSize, halfSize)
              context.arc(halfSize, -halfSize, size, Math.PI / 2, Math.PI)
              break
            case 180: // Bottom-right
              context.moveTo(halfSize, -halfSize)
              context.lineTo(halfSize, halfSize)
              context.lineTo(-halfSize, halfSize)
              context.arc(halfSize, halfSize, size, Math.PI, Math.PI * 1.5)
              break
            case 270: // Bottom-left
              context.moveTo(halfSize, halfSize)
              context.lineTo(-halfSize, halfSize)
              context.lineTo(-halfSize, -halfSize)
              context.arc(-halfSize, halfSize, size, Math.PI * 1.5, Math.PI * 2)
              break
          }

          context.closePath()
          context.fillStrokeShape(shape)
        },
        fill: color,
        stroke: '#000000',
        strokeWidth: 2
      })

      return shape
    },

    /**
     * Draw table on canvas
     */
    drawTable (table) {
      let shape

      if (table.shape === 'circle') {
        shape = new Konva.Circle({
          x: 0,
          y: 0,
          radius: table.width / 2,
          fill: table.color,
          stroke: '#000000',
          strokeWidth: 2
        })
      } else if (table.shape === 'oval') {
        // Oval shape using ellipse
        shape = new Konva.Ellipse({
          x: 0,
          y: 0,
          radiusX: table.width / 2,
          radiusY: table.height / 2,
          fill: table.color,
          stroke: '#000000',
          strokeWidth: 2
        })
      } else if (table.shape === 'corner') {
        // Corner table - quarter circle shape
        const cornerAngle = table.cornerAngle || 0
        shape = this.createCornerShape(table.width, table.height, table.color, cornerAngle)
      } else {
        shape = new Konva.Rect({
          x: -table.width / 2,
          y: -table.height / 2,
          width: table.width,
          height: table.height,
          fill: table.color,
          stroke: '#000000',
          strokeWidth: 2,
          cornerRadius: table.shape === 'square' ? 8 : 4
        })
      }

      // Group shape and text
      // Position at top-left, rotation will be around center using offset
      const group = new Konva.Group({
        id: table.id,
        x: table.x,
        y: table.y,
        width: table.width,
        height: table.height,
        rotation: table.rotation || 0,
        offsetX: -table.width / 2,
        offsetY: -table.height / 2,
        objectType: 'table',
        objectData: table,
        draggable: this.editMode
      })

      // Add invisible rectangle for transformer bounds
      const boundingRect = new Konva.Rect({
        x: -table.width / 2,
        y: -table.height / 2,
        width: table.width,
        height: table.height,
        fill: 'transparent',
        stroke: 'transparent',
        listening: false,
        name: 'bounding-rect'
      })
      group.add(boundingRect)

      group.add(shape)

      // Add chairs around the table
      this.addChairsToTable(group, table)

      // Get Firestore table data if available
      const firestoreTable = this.tables.find(t => t.id === table.tableId)
      const hasItems = firestoreTable && firestoreTable.items && firestoreTable.items.length > 0

      // In user mode with table data, show detailed info
      if (!this.editMode && hasItems && firestoreTable.update_time) {
        // Update table object with Firestore data to get calculated totalAmount
        table.updateFromTable(firestoreTable)
        const totalAmount = table.totalAmount

        // Header with time and color
        const headerHeight = 20
        const timeHeaderColor = this.getWaitingTimeColor(firestoreTable.update_time)
        const timeTextColor = this.getWaitingTimeTextColor(firestoreTable.update_time)

        const timeHeader = new Konva.Rect({
          x: -table.width / 2,
          y: -table.height / 2 - headerHeight,
          width: table.width,
          height: headerHeight,
          fill: timeHeaderColor,
          cornerRadius: table.shape === 'square' ? [8, 8, 0, 0] : [4, 4, 0, 0],
          name: 'time-header'
        })
        group.add(timeHeader)

        const timeText = new Konva.Text({
          x: -table.width / 2,
          y: -table.height / 2 - headerHeight + 3,
          width: table.width,
          text: this.showWaitingTime(firestoreTable.update_time),
          fontSize: 12,
          fontFamily: 'Arial',
          fontStyle: 'bold',
          fill: timeTextColor,
          align: 'center',
          name: 'time-text'
        })
        group.add(timeText)

        // Table name (centered)
        const tableName = new Konva.Text({
          x: -table.width / 2,
          y: -18,
          width: table.width,
          text: table.tableName || firestoreTable.name,
          fontSize: 14,
          fontFamily: 'Arial',
          fontStyle: 'bold',
          fill: '#FFFFFF',
          align: 'center'
        })
        group.add(tableName)

        // Amount (below name)
        const amountText = new Konva.Text({
          x: -table.width / 2,
          y: 2,
          width: table.width,
          text: this.formatCurrency(totalAmount),
          fontSize: 13,
          fontFamily: 'Arial',
          fill: '#FFFFFF',
          align: 'center'
        })
        group.add(amountText)

        // Add keyboard shortcut badge (user mode with data)
        if (this.keyboardShortcutsEnabled && (this.showShortcutBadges || this.showBadgesTemporarily) && table.shortcut) {
          const badgeSize = 24
          const padding = 4
          const badgeX = table.width / 2 - badgeSize - padding
          const badgeY = table.height / 2 - badgeSize - padding

          // Badge background
          const badgeRect = new Konva.Rect({
            x: badgeX,
            y: badgeY,
            width: badgeSize,
            height: badgeSize,
            fill: '#FFFFFF',
            cornerRadius: 4,
            name: 'shortcut-badge'
          })
          group.add(badgeRect)

          // Badge text
          const badgeText = new Konva.Text({
            x: badgeX,
            y: badgeY + 5,
            width: badgeSize,
            text: table.shortcut,
            fontSize: 11,
            fontFamily: 'Arial',
            fontStyle: 'bold',
            fill: '#2196F3',
            align: 'center'
          })
          group.add(badgeText)
        }
      } else {
        // Edit mode or no data - just show table name
        const text = new Konva.Text({
          x: -table.width / 2,
          y: -10,
          width: table.width,
          text: table.tableName,
          fontSize: 14,
          fontFamily: 'Arial',
          fill: '#FFFFFF',
          align: 'center'
        })
        group.add(text)
      }

      // Add keyboard shortcut badge (only in user mode)
      if (!this.editMode && this.keyboardShortcutsEnabled && (this.showShortcutBadges || this.showBadgesTemporarily) && table.shortcut) {
        const badgeSize = 24
        const padding = 4
        const badgeX = table.width / 2 - badgeSize - padding
        const badgeY = table.height / 2 - badgeSize - padding

        // Badge background
        const badgeRect = new Konva.Rect({
          x: badgeX,
          y: badgeY,
          width: badgeSize,
          height: badgeSize,
          fill: '#FFFFFF',
          cornerRadius: 4,
          name: 'shortcut-badge'
        })
        group.add(badgeRect)

        // Badge text
        const badgeText = new Konva.Text({
          x: badgeX,
          y: badgeY + 5,
          width: badgeSize,
          text: table.shortcut,
          fontSize: 11,
          fontFamily: 'Arial',
          fontStyle: 'bold',
          fill: '#2196F3', // Primary blue color
          align: 'center'
        })
        group.add(badgeText)
      }

      // Setup interactions
      this.setupObjectInteractions(group, table)

      this.layer.add(group)

      // Move tables to top (above areas and walls)
      group.moveToTop()

      this.konvaObjects.set(table.id, group)
    },

    /**
     * Draw door on canvas
     */
    drawDoor (door) {
      // Create group for door
      const group = new Konva.Group({
        id: door.id,
        x: door.x,
        y: door.y,
        width: door.width,
        height: door.height,
        rotation: door.rotation || 0,
        objectType: 'door',
        objectData: door,
        draggable: this.editMode
      })

      // Door frame - straight line (use height as thickness)
      const doorFrame = new Konva.Line({
        points: [0, 0, door.width, 0],
        stroke: door.color || '#000000',
        strokeWidth: door.height || 10,
        lineCap: 'round'
      })
      group.add(doorFrame)

      // Door swing arc - polukrug koji pokazuje otvaranje vrata
      let arc
      const radius = door.width

      // Ovisno o doorSwing property, postavi luk na odgovarajuću stranu
      switch (door.doorSwing) {
        case 'left-in':
          // Luk počinje od lijeve strane, otvara se prema unutra (gore)
          arc = new Konva.Arc({
            x: 0,
            y: 0,
            innerRadius: 0,
            outerRadius: radius,
            angle: 90,
            rotation: -90, // Rotacija da ide gore
            stroke: door.color || '#000000',
            strokeWidth: 1,
            dash: [5, 5]
          })
          break
        case 'left-out':
          // Luk počinje od lijeve strane, otvara se prema van (dolje)
          arc = new Konva.Arc({
            x: 0,
            y: 0,
            innerRadius: 0,
            outerRadius: radius,
            angle: 90,
            rotation: 0, // Rotacija da ide dolje
            stroke: door.color || '#000000',
            strokeWidth: 1,
            dash: [5, 5]
          })
          break
        case 'right-in':
          // Luk počinje od desne strane, otvara se prema unutra (gore)
          arc = new Konva.Arc({
            x: door.width,
            y: 0,
            innerRadius: 0,
            outerRadius: radius,
            angle: 90,
            rotation: 180, // Rotacija da ide gore desno
            stroke: door.color || '#000000',
            strokeWidth: 1,
            dash: [5, 5]
          })
          break
        case 'right-out':
          // Luk počinje od desne strane, otvara se prema van (dolje)
          arc = new Konva.Arc({
            x: door.width,
            y: 0,
            innerRadius: 0,
            outerRadius: radius,
            angle: 90,
            rotation: 90, // Rotacija da ide dolje desno
            stroke: door.color || '#000000',
            strokeWidth: 1,
            dash: [5, 5]
          })
          break
        default:
          // Default: left-in
          arc = new Konva.Arc({
            x: 0,
            y: 0,
            innerRadius: 0,
            outerRadius: radius,
            angle: 90,
            rotation: -90,
            stroke: door.color || '#000000',
            strokeWidth: 1,
            dash: [5, 5]
          })
      }

      group.add(arc)

      // Setup interactions
      this.setupObjectInteractions(group, door)

      this.layer.add(group)
      this.konvaObjects.set(door.id, group)
    },

    /**
     * Draw bar/counter on canvas
     */
    drawBar (bar) {
      // Create group for bar
      const group = new Konva.Group({
        id: bar.id,
        x: bar.x,
        y: bar.y,
        width: bar.width,
        height: bar.height,
        rotation: bar.rotation || 0,
        offsetX: bar.width / 2,
        offsetY: bar.height / 2,
        objectType: 'bar',
        objectData: bar,
        draggable: this.editMode
      })

      let shape

      // Draw based on shape type (coordinates relative to center due to offset)
      switch (bar.shape) {
        case 'rectangle':
          shape = new Konva.Rect({
            x: -bar.width / 2,
            y: -bar.height / 2,
            width: bar.width,
            height: bar.height,
            fill: bar.color || '#8B4513',
            stroke: bar.borderColor || '#654321',
            strokeWidth: 3
          })
          break

        case 'semicircle':
          // Half circle (top half)
          shape = new Konva.Arc({
            x: 0,
            y: bar.height / 2,
            innerRadius: 0,
            outerRadius: bar.width / 2,
            angle: 180,
            rotation: 180,
            fill: bar.color || '#8B4513',
            stroke: bar.borderColor || '#654321',
            strokeWidth: 3
          })
          break

        case 'semioval':
          // Half oval (top half) - using wedge as approximation
          shape = new Konva.Wedge({
            x: 0,
            y: bar.height / 2,
            radius: bar.width / 2,
            angle: 180,
            rotation: 180,
            fill: bar.color || '#8B4513',
            stroke: bar.borderColor || '#654321',
            strokeWidth: 3,
            scaleY: bar.height / (bar.width / 2) // Scale Y to create oval shape
          })
          break

        default:
          // Default to rectangle
          shape = new Konva.Rect({
            x: -bar.width / 2,
            y: -bar.height / 2,
            width: bar.width,
            height: bar.height,
            fill: bar.color || '#8B4513',
            stroke: bar.borderColor || '#654321',
            strokeWidth: 3
          })
      }

      group.add(shape)

      // Add keyboard shortcut badge (only in user mode)
      if (!this.editMode && this.keyboardShortcutsEnabled && (this.showShortcutBadges || this.showBadgesTemporarily) && bar.shortcut) {
        const badgeSize = 24
        const padding = 4
        const badgeX = bar.width / 2 - badgeSize - padding
        const badgeY = bar.height / 2 - badgeSize - padding

        // Badge background
        const badgeRect = new Konva.Rect({
          x: badgeX,
          y: badgeY,
          width: badgeSize,
          height: badgeSize,
          fill: '#FFFFFF',
          cornerRadius: 4,
          name: 'shortcut-badge'
        })
        group.add(badgeRect)

        // Badge text
        const badgeText = new Konva.Text({
          x: badgeX,
          y: badgeY + 5,
          width: badgeSize,
          text: bar.shortcut,
          fontSize: 11,
          fontFamily: 'Arial',
          fontStyle: 'bold',
          fill: '#2196F3', // Primary blue color
          align: 'center'
        })
        group.add(badgeText)
      }

      // Setup interactions
      this.setupObjectInteractions(group, bar)

      this.layer.add(group)
      this.konvaObjects.set(bar.id, group)
    },

    /**
     * Draw wall on canvas
     */
    drawWall (wall) {
      const points = []
      wall.points.forEach(p => {
        points.push(p.x, p.y)
      })

      const line = new Konva.Line({
        id: wall.id,
        points: points,
        stroke: wall.color,
        strokeWidth: wall.thickness,
        lineCap: 'round',
        lineJoin: 'round',
        objectType: 'wall',
        objectData: wall,
        draggable: this.editMode
      })

      // Setup interactions
      this.setupObjectInteractions(line, wall)

      this.layer.add(line)
      this.konvaObjects.set(wall.id, line)
    },

    /**
     * Draw area on canvas
     */
    drawArea (area) {
      const group = new Konva.Group({
        id: area.id,
        x: area.x,
        y: area.y,
        width: area.width,
        height: area.height,
        objectType: 'area',
        objectData: area,
        draggable: this.editMode
      })

      // Background rectangle (relative to group position)
      const rect = new Konva.Rect({
        x: 0,
        y: 0,
        width: area.width,
        height: area.height,
        fill: area.backgroundColor,
        stroke: area.borderColor,
        strokeWidth: 2,
        opacity: area.opacity,
        objectType: 'area',
        objectData: area
      })
      group.add(rect)

      // Add pattern overlay if pattern is not 'none'
      if (area.pattern && area.pattern !== 'none') {
        this.drawAreaPattern(group, area)
      }

      // Add invisible rectangle for transformer bounds (relative to group)
      const boundingRect = new Konva.Rect({
        x: 0,
        y: 0,
        width: area.width,
        height: area.height,
        fill: 'transparent',
        stroke: 'transparent',
        listening: false,
        name: 'bounding-rect'
      })
      group.add(boundingRect)

      // Text label (relative to group)
      const text = new Konva.Text({
        x: 0,
        y: area.height / 2 - area.fontSize / 2,
        width: area.width,
        text: area.label,
        fontSize: area.fontSize,
        fontFamily: 'Arial',
        fill: area.textColor,
        align: 'center'
      })
      group.add(text)

      this.layer.add(group)

      // Move areas to bottom (below tables and other objects)
      group.moveToBottom()

      // Setup interactions AFTER adding to layer and moving to bottom
      this.setupObjectInteractions(group, area)

      this.konvaObjects.set(area.id, group)
    },

    /**
     * Draw text box on canvas
     */
    drawTextBox (textBox) {
      const group = new Konva.Group({
        id: textBox.id,
        x: textBox.x,
        y: textBox.y,
        width: textBox.width,
        height: textBox.height,
        rotation: textBox.rotation || 0,
        offsetX: textBox.width / 2,
        offsetY: textBox.height / 2,
        objectType: 'textbox',
        objectData: textBox,
        draggable: this.editMode
      })

      // Background rectangle
      const bgRect = new Konva.Rect({
        x: -textBox.width / 2,
        y: -textBox.height / 2,
        width: textBox.width,
        height: textBox.height,
        fill: textBox.backgroundColor === 'transparent' ? undefined : textBox.backgroundColor,
        stroke: textBox.borderColor,
        strokeWidth: textBox.backgroundColor === 'transparent' ? 0 : 2
      })
      group.add(bgRect)

      // Build font style string
      let fontStyle = ''
      if (textBox.fontStyle.includes('bold')) fontStyle += 'bold '
      if (textBox.fontStyle.includes('italic')) fontStyle += 'italic '

      // Text element
      const text = new Konva.Text({
        x: -textBox.width / 2 + textBox.padding,
        y: -textBox.height / 2 + textBox.padding,
        width: textBox.width - textBox.padding * 2,
        height: textBox.height - textBox.padding * 2,
        text: textBox.text,
        fontSize: textBox.fontSize,
        fontFamily: textBox.fontFamily,
        fontStyle: fontStyle.trim(),
        textDecoration: textBox.textDecoration,
        fill: textBox.textColor,
        align: textBox.align,
        verticalAlign: 'middle'
      })
      group.add(text)

      // Setup interactions
      this.setupObjectInteractions(group, textBox)

      this.layer.add(group)
      // Move text boxes to top (same level as tables)
      group.moveToTop()

      this.konvaObjects.set(textBox.id, group)
    },

    /**
     * Setup object interactions (click, drag, etc.)
     */
    setupObjectInteractions (shape, objectData) {
      // Click and tap handler (for both mouse and touch)
      const handleClickOrTap = (e) => {
        e.cancelBubble = true

        if (this.editMode) {
          // Delete tool
          if (this.activeTool === 'delete') {
            this.selectedObject = objectData
            this.deleteSelectedObject()
            return
          }

          // Multi-select with Shift
          if (this.isShiftPressed) {
            const currentNodes = this.transformer.nodes()
            if (currentNodes.includes(shape)) {
              // Remove from selection
              this.transformer.nodes(currentNodes.filter(n => n !== shape))
            } else {
              // Add to selection
              this.transformer.nodes([...currentNodes, shape])
            }
            this.transformer.getLayer().batchDraw()
          } else {
            // Single select
            this.selectObject(shape)
          }
        } else {
          // User mode - handle table/bar click
          if ((objectData.type === 'table' || objectData.type === 'bar') && objectData.tableId) {
            this.$emit('table-clicked', objectData.tableId)
          }
        }
      }

      shape.on('click', handleClickOrTap)
      shape.on('tap', handleClickOrTap)

      // Drag start handler
      shape.on('dragstart', (e) => {
        if (!this.editMode) {
          e.currentTarget.stopDrag()
        } else {
          // Set dragging flag to prevent re-render
          this.isDragging = true

          // For areas, completely detach from transformer to prevent conflicts
          if (objectData.type === 'area' && this.transformer) {
            this.transformer.nodes([])
            this.transformer.detach()
          } else if (this.transformer && this.transformer.nodes().includes(shape)) {
            // For other objects, just clear nodes
            this.transformer.nodes([])
          }
        }
      })

      // Drag move handler - update position in real-time
      shape.on('dragmove', (e) => {
        // Update object data and selected object in real-time
        const pos = shape.position()
        // With negative offset, pos is already top-left
        objectData.x = pos.x
        objectData.y = pos.y

        // Update selected object to refresh properties panel
        if (this.selectedObject && this.selectedObject.id === objectData.id) {
          this.selectedObject.x = objectData.x
          this.selectedObject.y = objectData.y
        }
      })

      // Drag end handler
      shape.on('dragend', (e) => {
        const pos = shape.position()
        // With negative offset, pos is already top-left
        objectData.x = pos.x
        objectData.y = pos.y

        if (this.snapToGrid) {
          objectData.x = snapToGrid(objectData.x, this.gridSize)
          objectData.y = snapToGrid(objectData.y, this.gridSize)
          shape.position({
            x: objectData.x,
            y: objectData.y
          })
        }

        // Clear dragging flag
        this.isDragging = false

        // Re-attach transformer after drag is complete
        this.$nextTick(() => {
          if (this.transformer && this.editMode && objectData.type !== 'area') {
            try {
              this.transformer.nodes([shape])
              this.transformer.getLayer().batchDraw()
            } catch (err) {
              console.warn('Transformer re-attach failed:', err)
            }
          }
        })

        this.$emit('object-updated', objectData.toJSON())
      })

      // Transform handler - update size and rotation during transform
      shape.on('transform', () => {
        // Update object data with new dimensions
        const scaleX = shape.scaleX()
        const scaleY = shape.scaleY()

        // Get the new width and height based on scale
        const newWidth = Math.max(5, shape.width() * scaleX)
        const newHeight = Math.max(5, shape.height() * scaleY)

        objectData.width = newWidth
        objectData.height = newHeight
        objectData.rotation = shape.rotation()

        // Reset scale to 1 (size is now in width/height)
        shape.scaleX(1)
        shape.scaleY(1)
        shape.width(newWidth)
        shape.height(newHeight)

        // Update selected object to refresh properties panel in real-time
        if (this.selectedObject && this.selectedObject.id === objectData.id) {
          this.selectedObject.width = newWidth
          this.selectedObject.height = newHeight
          this.selectedObject.rotation = objectData.rotation
        }
      })

      // Transform end handler
      shape.on('transformend', () => {
        const scaleX = shape.scaleX()
        const scaleY = shape.scaleY()

        // Final update with scale reset
        const newWidth = Math.max(5, shape.width() * scaleX)
        const newHeight = Math.max(5, shape.height() * scaleY)

        objectData.width = newWidth
        objectData.height = newHeight
        objectData.rotation = shape.rotation()

        shape.scaleX(1)
        shape.scaleY(1)
        shape.width(newWidth)
        shape.height(newHeight)

        // For circle shapes, keep it circular by matching height to width
        if (objectData.type === 'table' && objectData.shape === 'circle') {
          objectData.height = newWidth // Keep it circular
          shape.height(newWidth)
        }

        this.$emit('object-updated', objectData.toJSON())
      })

      // Hover cursor
      shape.on('mouseenter', () => {
        if (this.editMode) {
          this.stage.container().style.cursor = 'move'
        } else if (objectData.type === 'table') {
          this.stage.container().style.cursor = 'pointer'
        }
      })

      shape.on('mouseleave', () => {
        this.stage.container().style.cursor = 'default'
      })
    },

    /**
     * Select object for editing
     */
    selectObject (shape) {
      // Clear previous selection highlight
      if (this.selectedObject) {
        const prevShape = this.konvaObjects.get(this.selectedObject.id)
        if (prevShape) {
          this.removeSelectionHighlight(prevShape)
        }
      }

      // Set new selection
      this.selectedObject = shape.attrs.objectData

      // Add shadow to selected object
      if (shape.getChildren) {
        // It's a Group - apply to children
        shape.getChildren().forEach(child => {
          if (child.shadowColor) {
            child.shadowColor('#2196F3') // Blue shadow
            child.shadowBlur(20)
            child.shadowOpacity(0.8)
            child.shadowOffset({ x: 0, y: 0 })
          }
        })
      } else if (shape.shadowColor) {
        // It's a single shape - apply directly
        shape.shadowColor('#2196F3')
        shape.shadowBlur(20)
        shape.shadowOpacity(0.8)
        shape.shadowOffset({ x: 0, y: 0 })
      }

      // Remove old resize handles
      this.removeResizeHandles()

      // Add custom resize handles
      this.addResizeHandles(shape)

      this.layer.batchDraw()

      // Emit event to parent
      this.$emit('object-selected', this.selectedObject)
    },

    /**
     * Add custom resize handles to selected object
     */
    addResizeHandles (shape) {
      const objectData = shape.attrs.objectData
      if (!objectData || objectData.type === 'wall' || objectData.type === 'door') return // Don't add handles for walls and doors

      const width = objectData.width
      const height = objectData.height

      // Store resize handles for later removal
      this.resizeHandles = []

      // Define handle positions in local coordinates (relative to shape's coordinate system)
      // With negative offset, (0, 0) is at the center of the shape
      const handlePositionsLocal = [
        { name: 'top-left', x: -width / 2, y: -height / 2, cursor: 'nw-resize' },
        { name: 'top-right', x: width / 2, y: -height / 2, cursor: 'ne-resize' },
        { name: 'bottom-left', x: -width / 2, y: height / 2, cursor: 'sw-resize' },
        { name: 'bottom-right', x: width / 2, y: height / 2, cursor: 'se-resize' },
        { name: 'top-center', x: 0, y: -height / 2, cursor: 'n-resize' },
        { name: 'bottom-center', x: 0, y: height / 2, cursor: 's-resize' },
        { name: 'middle-left', x: -width / 2, y: 0, cursor: 'w-resize' },
        { name: 'middle-right', x: width / 2, y: 0, cursor: 'e-resize' }
      ]

      // Transform local positions to global positions
      const handlePositions = handlePositionsLocal.map(pos => {
        // Get the absolute position by transforming local coordinates
        const point = shape.getAbsoluteTransform().point({ x: pos.x, y: pos.y })
        return {
          name: pos.name,
          x: point.x,
          y: point.y,
          cursor: pos.cursor
        }
      })

      handlePositions.forEach(pos => {
        const handle = new Konva.Circle({
          x: pos.x,
          y: pos.y,
          radius: 6,
          fill: '#ffffff',
          stroke: '#0066cc',
          strokeWidth: 2,
          draggable: true,
          name: 'resize-handle',
          handleName: pos.name
        })

        // Change cursor on hover
        handle.on('mouseenter', () => {
          this.stage.container().style.cursor = pos.cursor
        })

        handle.on('mouseleave', () => {
          this.stage.container().style.cursor = 'default'
        })

        // Handle dragging to resize
        handle.on('dragmove', () => {
          this.updateResizePreview(shape, handle, pos.name)
        })

        handle.on('dragend', () => {
          // Apply final resize
          this.applyResize(shape, handle, pos.name)
          // Emit update event
          this.$emit('object-updated', objectData.toJSON())
        })

        this.layer.add(handle)
        this.resizeHandles.push(handle)
      })

      // Add rotation handle (circle above the object)
      // Position it 30px above the top-center in local coordinates
      const rotationHandleLocal = { x: 0, y: -height / 2 - 30 }
      const rotationHandleGlobal = shape.getAbsoluteTransform().point(rotationHandleLocal)

      const rotationHandle = new Konva.Circle({
        x: rotationHandleGlobal.x,
        y: rotationHandleGlobal.y,
        radius: 6,
        fill: '#ffffff',
        stroke: '#00cc66',
        strokeWidth: 2,
        draggable: true,
        name: 'rotation-handle'
      })

      rotationHandle.on('mouseenter', () => {
        this.stage.container().style.cursor = 'grab'
      })

      rotationHandle.on('mouseleave', () => {
        this.stage.container().style.cursor = 'default'
      })

      // Handle rotation dragging
      rotationHandle.on('dragmove', () => {
        const handlePos = rotationHandle.position()
        const shapePos = shape.position()

        // Calculate angle between shape center and handle
        const dx = handlePos.x - shapePos.x
        const dy = handlePos.y - shapePos.y
        const angle = Math.atan2(dy, dx)

        // Convert to degrees and adjust (0 degrees should be pointing up)
        let rotation = (angle * 180 / Math.PI) + 90

        // Snap to 15 degree increments if shift is not pressed
        if (!this.isShiftPressed) {
          rotation = Math.round(rotation / 15) * 15
        }

        // Update shape rotation
        shape.rotation(rotation)
        objectData.rotation = rotation

        // Update selected object for properties panel
        if (this.selectedObject && this.selectedObject.id === objectData.id) {
          this.selectedObject.rotation = rotation
        }

        // Update other handles positions
        this.updateHandlePositions(objectData.x, objectData.y, objectData.width, objectData.height, rotationHandle)

        this.layer.batchDraw()
      })

      rotationHandle.on('dragend', () => {
        // Redraw shape to apply rotation
        this.redrawShape(shape)
        // Emit update event
        this.$emit('object-updated', objectData.toJSON())
      })

      this.layer.add(rotationHandle)
      this.resizeHandles.push(rotationHandle)

      this.layer.batchDraw()
    },

    /**
     * Update resize preview (during drag) - just move other handles
     */
    updateResizePreview (shape, handle, handleName) {
      const objectData = shape.attrs.objectData
      const handlePos = handle.position()

      // With negative offset, shape.x() and shape.y() are already the top-left position
      const currentLeft = shape.x()
      const currentTop = shape.y()
      const currentRight = currentLeft + objectData.width
      const currentBottom = currentTop + objectData.height

      // Calculate current center position
      const centerX = currentLeft + objectData.width / 2
      const centerY = currentTop + objectData.height / 2

      // Calculate new dimensions and center position
      let newWidth = objectData.width
      let newHeight = objectData.height
      let newCenterX = centerX
      let newCenterY = centerY

      switch (handleName) {
        case 'bottom-right':
          newWidth = Math.max(20, (handlePos.x - currentLeft))
          newHeight = Math.max(20, (handlePos.y - currentTop))
          newCenterX = currentLeft + newWidth / 2
          newCenterY = currentTop + newHeight / 2
          break
        case 'bottom-left':
          newWidth = Math.max(20, (currentRight - handlePos.x))
          newHeight = Math.max(20, (handlePos.y - currentTop))
          newCenterX = handlePos.x + newWidth / 2
          newCenterY = currentTop + newHeight / 2
          break
        case 'top-right':
          newWidth = Math.max(20, (handlePos.x - currentLeft))
          newHeight = Math.max(20, (currentBottom - handlePos.y))
          newCenterX = currentLeft + newWidth / 2
          newCenterY = handlePos.y + newHeight / 2
          break
        case 'top-left':
          newWidth = Math.max(20, (currentRight - handlePos.x))
          newHeight = Math.max(20, (currentBottom - handlePos.y))
          newCenterX = handlePos.x + newWidth / 2
          newCenterY = handlePos.y + newHeight / 2
          break
        case 'top-center':
          newHeight = Math.max(20, (currentBottom - handlePos.y))
          newCenterY = handlePos.y + newHeight / 2
          break
        case 'bottom-center':
          newHeight = Math.max(20, (handlePos.y - currentTop))
          newCenterY = currentTop + newHeight / 2
          break
        case 'middle-left':
          newWidth = Math.max(20, (currentRight - handlePos.x))
          newCenterX = handlePos.x + newWidth / 2
          break
        case 'middle-right':
          newWidth = Math.max(20, (handlePos.x - currentLeft))
          newCenterX = currentLeft + newWidth / 2
          break
      }

      // Calculate top-left for handle positioning
      const newLeft = newCenterX - newWidth / 2
      const newTop = newCenterY - newHeight / 2

      // Update other handles positions to show preview
      this.updateHandlePositions(newLeft, newTop, newWidth, newHeight, handle)

      // Update selected object for properties panel preview (use top-left coordinates)
      if (this.selectedObject && this.selectedObject.id === objectData.id) {
        this.selectedObject.width = newWidth
        this.selectedObject.height = newHeight
        this.selectedObject.x = newLeft
        this.selectedObject.y = newTop
      }
    },

    /**
     * Apply final resize (on drag end)
     */
    applyResize (shape, handle, handleName) {
      const objectData = shape.attrs.objectData
      const handlePos = handle.position()

      // With negative offset, shape.x() and shape.y() are already the top-left position
      const currentLeft = shape.x()
      const currentTop = shape.y()
      const currentRight = currentLeft + objectData.width
      const currentBottom = currentTop + objectData.height

      // Calculate current center position
      const centerX = currentLeft + objectData.width / 2
      const centerY = currentTop + objectData.height / 2

      // Calculate new dimensions and center position
      let newWidth = objectData.width
      let newHeight = objectData.height
      let newCenterX = centerX
      let newCenterY = centerY

      switch (handleName) {
        case 'bottom-right':
          newWidth = Math.max(20, (handlePos.x - currentLeft))
          newHeight = Math.max(20, (handlePos.y - currentTop))
          newCenterX = currentLeft + newWidth / 2
          newCenterY = currentTop + newHeight / 2
          break
        case 'bottom-left':
          newWidth = Math.max(20, (currentRight - handlePos.x))
          newHeight = Math.max(20, (handlePos.y - currentTop))
          newCenterX = handlePos.x + newWidth / 2
          newCenterY = currentTop + newHeight / 2
          break
        case 'top-right':
          newWidth = Math.max(20, (handlePos.x - currentLeft))
          newHeight = Math.max(20, (currentBottom - handlePos.y))
          newCenterX = currentLeft + newWidth / 2
          newCenterY = handlePos.y + newHeight / 2
          break
        case 'top-left':
          newWidth = Math.max(20, (currentRight - handlePos.x))
          newHeight = Math.max(20, (currentBottom - handlePos.y))
          newCenterX = handlePos.x + newWidth / 2
          newCenterY = handlePos.y + newHeight / 2
          break
        case 'top-center':
          newHeight = Math.max(20, (currentBottom - handlePos.y))
          newCenterY = handlePos.y + newHeight / 2
          break
        case 'bottom-center':
          newHeight = Math.max(20, (handlePos.y - currentTop))
          newCenterY = currentTop + newHeight / 2
          break
        case 'middle-left':
          newWidth = Math.max(20, (currentRight - handlePos.x))
          newCenterX = handlePos.x + newWidth / 2
          break
        case 'middle-right':
          newWidth = Math.max(20, (handlePos.x - currentLeft))
          newCenterX = currentLeft + newWidth / 2
          break
      }

      // Calculate new top-left position
      const newLeft = newCenterX - newWidth / 2
      const newTop = newCenterY - newHeight / 2

      // Update object data with new dimensions
      objectData.width = newWidth
      objectData.height = newHeight
      // Store top-left position in objectData
      objectData.x = newLeft
      objectData.y = newTop

      // Update shape position (reference point with negative offset)
      // With negative offset, reference point is at top-left
      const newOffsetX = -newWidth / 2
      const newOffsetY = -newHeight / 2
      shape.x(newLeft)
      shape.y(newTop)
      shape.offsetX(newOffsetX)
      shape.offsetY(newOffsetY)

      // Update selected object for properties panel (use top-left coordinates)
      if (this.selectedObject && this.selectedObject.id === objectData.id) {
        this.selectedObject.width = objectData.width
        this.selectedObject.height = objectData.height
        this.selectedObject.x = objectData.x
        this.selectedObject.y = objectData.y
      }

      // Redraw the shape with new dimensions
      this.redrawShape(shape)
    },

    /**
     * Update positions of all handles during resize preview
     */
    updateHandlePositions (x, y, width, height, activeHandle) {
      if (!this.resizeHandles) return

      this.resizeHandles.forEach(handle => {
        if (handle === activeHandle) return // Don't move the handle being dragged

        const handleName = handle.attrs.handleName
        if (!handleName) return // Skip rotation handle

        switch (handleName) {
          case 'top-left':
            handle.position({ x, y })
            break
          case 'top-right':
            handle.position({ x: x + width, y })
            break
          case 'bottom-left':
            handle.position({ x, y: y + height })
            break
          case 'bottom-right':
            handle.position({ x: x + width, y: y + height })
            break
          case 'top-center':
            handle.position({ x: x + width / 2, y })
            break
          case 'bottom-center':
            handle.position({ x: x + width / 2, y: y + height })
            break
          case 'middle-left':
            handle.position({ x, y: y + height / 2 })
            break
          case 'middle-right':
            handle.position({ x: x + width, y: y + height / 2 })
            break
        }
      })

      this.layer.batchDraw()
    },

    /**
     * Redraw shape with current dimensions
     */
    redrawShape (shape) {
      const objectData = shape.attrs.objectData

      // Remove and redraw based on type
      const id = objectData.id
      shape.destroy()
      this.konvaObjects.delete(id)

      if (objectData.type === 'table') {
        this.drawTable(objectData)
      } else if (objectData.type === 'area') {
        this.drawArea(objectData)
      }

      // Reselect the newly drawn object
      this.$nextTick(() => {
        const newShape = this.konvaObjects.get(id)
        if (newShape) {
          this.selectedObject = newShape.attrs.objectData
          this.removeResizeHandles()
          this.addResizeHandles(newShape)
        }
      })
    },

    /**
     * Remove resize handles from canvas
     */
    removeResizeHandles () {
      if (this.resizeHandles) {
        this.resizeHandles.forEach(handle => handle.destroy())
        this.resizeHandles = []
      }
    },

    /**
     * Add visual highlight to selected object
     */
    addSelectionHighlight (group) {
      if (!group) return

      // Add shadow/glow effect to all children in the group
      if (group.getChildren) {
        group.getChildren().forEach(child => {
          // Only add shadow to main shapes (not text or headers)
          if (child.getClassName() === 'Circle' || child.getClassName() === 'Rect') {
            // Store original shadow settings
            if (!child.attrs.originalShadow) {
              child.attrs.originalShadow = {
                color: child.shadowColor(),
                blur: child.shadowBlur(),
                opacity: child.shadowOpacity()
              }
            }
            child.shadowColor('rgba(33, 150, 243, 0.8)')
            child.shadowBlur(20)
            child.shadowOffset({ x: 0, y: 0 })
            child.shadowOpacity(1)
          }
        })
      }

      const layer = group.getLayer()
      if (layer) {
        layer.batchDraw()
      }
    },

    /**
     * Remove visual highlight from object
     */
    removeSelectionHighlight (shape) {
      if (!shape) return

      // Remove shadow
      if (shape.getChildren) {
        // It's a Group - remove from children
        shape.getChildren().forEach(child => {
          if (child.shadowColor) {
            child.shadowColor(null)
            child.shadowBlur(0)
            child.shadowOpacity(0)
          }
        })
      } else if (shape.shadowColor) {
        // It's a single shape - remove directly
        shape.shadowColor(null)
        shape.shadowBlur(0)
        shape.shadowOpacity(0)
      }

      const layer = shape.getLayer()
      if (layer) {
        layer.batchDraw()
      }
    },

    /**
     * Update visual representation of an object
     */
    updateObjectVisual (objectData) {
      const konvaObj = this.konvaObjects.get(objectData.id)
      if (konvaObj) {
        // If this object is currently selected in transformer, deselect it first
        const transformerNodes = this.transformer.nodes()
        if (transformerNodes.includes(konvaObj)) {
          this.transformer.nodes([])
        }

        // Remove old object
        konvaObj.destroy()
        this.konvaObjects.delete(objectData.id)

        // Redraw with updated data
        if (objectData.type === 'table') {
          const table = new TableObject(objectData)
          this.drawTable(table)
        } else if (objectData.type === 'wall') {
          const wall = new WallObject(objectData)
          this.drawWall(wall)
        } else if (objectData.type === 'door') {
          const door = new DoorObject(objectData)
          this.drawDoor(door)
        } else if (objectData.type === 'bar') {
          const bar = new BarObject(objectData)
          this.drawBar(bar)
        } else if (objectData.type === 'area') {
          const area = new AreaObject(objectData)
          this.drawArea(area)
        }

        this.layer.batchDraw()
      }
    },

    /**
     * Update cursor based on active tool
     */
    updateCursor (tool) {
      if (!this.stage) return

      const container = this.stage.container()
      if (tool === 'select') {
        container.style.cursor = 'default'
      } else if (tool.startsWith('add-')) {
        container.style.cursor = 'crosshair'
      } else if (tool === 'delete') {
        container.style.cursor = 'not-allowed'
      }
    },

    /**
     * Update table statuses from Firestore data
     */
    updateTableStatuses () {
      if (!this.room || !this.room.objects) return

      // In user mode, re-render all tables to show updated data
      if (!this.editMode) {
        this.renderObjects()
        return
      }

      // In edit mode, just update colors
      this.room.objects.forEach(objData => {
        if (objData.type === 'table') {
          const table = new TableObject(objData)
          const firestoreTable = this.tables.find(t => t.id === table.tableId)

          if (firestoreTable) {
            table.updateFromTable(firestoreTable)

            // Update Konva object
            const konvaObj = this.konvaObjects.get(table.id)
            if (konvaObj) {
              const shape = konvaObj.children[0] // First child is the shape
              if (shape) {
                shape.fill(table.color)
              }
              konvaObj.attrs.objectData = table
              this.layer.batchDraw()
            }
          }
        }
      })
    },

    /**
     * Reset stage position
     */
    resetPosition () {
      if (this.stage) {
        this.stage.position({ x: 0, y: 0 })
        this.stage.batchDraw()
      }
    },

    /**
     * Format currency
     */
    formatCurrency (amount) {
      // Amounts are stored in cents, divide by 100
      const amountInEuros = amount / 100
      return new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: 'EUR'
      }).format(amountInEuros)
    },

    /**
     * Get status color
     */
    getStatusColor (status) {
      switch (status) {
        case 'available':
          return 'blue'
        case 'occupied':
          return 'purple'
        case 'reserved':
          return 'orange'
        case 'locked':
          return 'grey'
        default:
          return 'grey'
      }
    },

    /**
     * Get status text
     */
    getStatusText (status) {
      switch (status) {
        case 'available':
          return this.$t('$vuetify.floorPlan.available')
        case 'occupied':
          return this.$t('$vuetify.floorPlan.occupied')
        case 'reserved':
          return this.$t('$vuetify.floorPlan.reserved')
        case 'locked':
          return this.$t('$vuetify.floorPlan.locked')
        default:
          return status
      }
    },

    /**
     * Calculate waiting time for a table
     */
    showWaitingTime (updateDate, mode = false) {
      const diff = new Date() - new Date(updateDate)
      const twoD = x => `${Math.floor(x)}`.padStart(2, '0')
      const HH = twoD((diff / (60 * 60 * 1000)) % 24)
      const mm = twoD((diff / (60 * 1000)) % 60)
      const ss = twoD((diff / 1000) % 60)
      if (mode) return diff
      else return `${HH}:${mm}:${ss}`
    },

    /**
     * Get waiting time background color based on duration
     */
    getWaitingTimeColor (updateDate) {
      const diff = new Date() - new Date(updateDate)
      if (diff < 1800000) { // < 30 minutes
        return '#8ff4a2' // green
      } else if (diff < 3600000) { // < 60 minutes
        return '#edf68b' // yellow
      } else { // >= 60 minutes
        return '#e898a0' // red
      }
    },

    /**
     * Get waiting time text color based on duration
     */
    getWaitingTimeTextColor (updateDate) {
      const diff = new Date() - new Date(updateDate)
      if (diff >= 3600000) { // >= 60 minutes
        return '#ffffff' // white text for red background
      }
      return '#000000' // black text for green/yellow backgrounds
    },

    /**
     * Start timer for updating table waiting times
     */
    startTimer () {
      if (this.updateTimer) {
        clearInterval(this.updateTimer)
      }
      if (!this.editMode && this.tables.length > 0) {
        this.updateTimer = setInterval(() => {
          this.timerCount++
          this.updateTableVisuals()
        }, 1000)
      }
    },

    /**
     * Stop timer
     */
    stopTimer () {
      if (this.updateTimer) {
        clearInterval(this.updateTimer)
        this.updateTimer = null
      }
    },

    /**
     * Update table visuals (time, colors, etc.)
     */
    updateTableVisuals () {
      if (!this.room || !this.room.objects || this.editMode) return

      this.room.objects.forEach(objData => {
        if (objData.type === 'table') {
          const table = new TableObject(objData)
          const firestoreTable = this.tables.find(t => t.id === table.tableId)

          if (firestoreTable && firestoreTable.items && firestoreTable.items.length > 0) {
            const konvaObj = this.konvaObjects.get(table.id)
            if (konvaObj) {
              // Update time text if it exists
              const timeText = konvaObj.findOne('.time-text')
              if (timeText && firestoreTable.update_time) {
                timeText.text(this.showWaitingTime(firestoreTable.update_time))
              }

              // Update header color
              const headerRect = konvaObj.findOne('.time-header')
              if (headerRect && firestoreTable.update_time) {
                headerRect.fill(this.getWaitingTimeColor(firestoreTable.update_time))
              }

              this.layer.batchDraw()
            }
          }
        }
      })
    },

    /**
     * Update background style (color and pattern)
     */
    updateBackgroundStyle () {
      if (!this.backgroundLayer || !this.stage) return

      // Clear existing background
      this.backgroundLayer.destroyChildren()

      const width = this.stage.width()
      const height = this.stage.height()

      // Create background rectangle
      const bgRect = new Konva.Rect({
        x: 0,
        y: 0,
        width: width * 10, // Make it large to cover panning
        height: height * 10,
        offsetX: width * 4.5,
        offsetY: height * 4.5,
        fill: this.backgroundColor
      })
      this.backgroundLayer.add(bgRect)

      // Add pattern overlay based on selected pattern
      if (this.backgroundPattern !== 'none') {
        this.drawBackgroundPattern(width, height)
      }

      this.backgroundLayer.batchDraw()
    },

    /**
     * Draw pattern overlay on background
     */
    drawBackgroundPattern (width, height) {
      const patternWidth = width * 10
      const patternHeight = height * 10
      const offsetX = width * 4.5
      const offsetY = height * 4.5

      switch (this.backgroundPattern) {
        case 'wood': {
          // Create vertical wood planks pattern
          const patternCanvas = document.createElement('canvas')
          const patternSize = 400
          patternCanvas.width = patternSize
          patternCanvas.height = patternSize
          const ctx = patternCanvas.getContext('2d')

          const plankWidth = 50
          const colors = [
            '#8B7355', // Medium brown
            '#A0826D', // Light brown
            '#7D6752', // Dark brown
            '#96795E', // Tan brown
            '#8A7456', // Medium tan
            '#7A6A52' // Dark tan
          ]

          // Draw vertical planks
          let x = 0
          while (x < patternSize) {
            const baseColor = colors[Math.floor(Math.random() * colors.length)]

            // Draw plank
            ctx.fillStyle = baseColor
            ctx.fillRect(x, 0, plankWidth, patternSize)

            // Add vertical wood grain texture
            for (let i = 0; i < 8; i++) {
              ctx.strokeStyle = `rgba(0, 0, 0, ${0.05 + Math.random() * 0.03})`
              ctx.lineWidth = 0.5 + Math.random() * 0.5
              ctx.beginPath()
              const grainX = x + Math.random() * plankWidth
              ctx.moveTo(grainX, 0)
              ctx.lineTo(grainX, patternSize)
              ctx.stroke()
            }

            // Dark edge between planks
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)'
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.moveTo(x, 0)
            ctx.lineTo(x, patternSize)
            ctx.stroke()

            x += plankWidth
          }

          // Use pattern as fillPatternImage
          const rect = new Konva.Rect({
            x: -offsetX,
            y: -offsetY,
            width: patternWidth,
            height: patternHeight,
            fillPatternImage: patternCanvas,
            fillPatternRepeat: 'repeat',
            fillPatternScale: { x: 1, y: 1 },
            opacity: 0.3
          })
          this.backgroundLayer.add(rect)
          break
        }

        case 'tile': {
          // Checkerboard pattern
          const tileSize = 40
          for (let y = 0; y < patternHeight; y += tileSize) {
            for (let x = 0; x < patternWidth; x += tileSize) {
              if ((Math.floor(x / tileSize) + Math.floor(y / tileSize)) % 2 === 0) {
                const tile = new Konva.Rect({
                  x: x,
                  y: y,
                  width: tileSize,
                  height: tileSize,
                  fill: 'rgba(0,0,0,0.03)',
                  offsetX: offsetX,
                  offsetY: offsetY
                })
                this.backgroundLayer.add(tile)
              }
            }
          }
          break
        }

        case 'carpet':
          // Diagonal lines pattern
          for (let i = -patternHeight; i < patternWidth + patternHeight; i += 20) {
            const line1 = new Konva.Line({
              points: [i, 0, i + patternHeight, patternHeight],
              stroke: 'rgba(139,0,0,0.05)',
              strokeWidth: 10,
              offsetX: offsetX,
              offsetY: offsetY
            })
            this.backgroundLayer.add(line1)

            const line2 = new Konva.Line({
              points: [i, 0, i - patternHeight, patternHeight],
              stroke: 'rgba(139,0,0,0.03)',
              strokeWidth: 10,
              offsetX: offsetX,
              offsetY: offsetY
            })
            this.backgroundLayer.add(line2)
          }
          break

        case 'marble': {
          // Marble effect with circles
          const marbleSpots = [
            { x: patternWidth * 0.2, y: patternHeight * 0.5, radius: 200, opacity: 0.15 },
            { x: patternWidth * 0.8, y: patternHeight * 0.8, radius: 150, opacity: 0.1 },
            { x: patternWidth * 0.4, y: patternHeight * 0.2, radius: 180, opacity: 0.12 },
            { x: patternWidth * 0.6, y: patternHeight * 0.6, radius: 160, opacity: 0.08 }
          ]
          marbleSpots.forEach(spot => {
            const circle = new Konva.Circle({
              x: spot.x,
              y: spot.y,
              radius: spot.radius,
              fill: `rgba(200,200,200,${spot.opacity})`,
              offsetX: offsetX,
              offsetY: offsetY
            })
            this.backgroundLayer.add(circle)
          })
          break
        }

        case 'concrete':
          // Grid pattern
          for (let i = 0; i < patternWidth; i += 50) {
            const vLine = new Konva.Line({
              points: [i, 0, i, patternHeight],
              stroke: 'rgba(0,0,0,0.03)',
              strokeWidth: 1,
              offsetX: offsetX,
              offsetY: offsetY
            })
            this.backgroundLayer.add(vLine)
          }
          for (let i = 0; i < patternHeight; i += 50) {
            const hLine = new Konva.Line({
              points: [0, i, patternWidth, i],
              stroke: 'rgba(0,0,0,0.03)',
              strokeWidth: 1,
              offsetX: offsetX,
              offsetY: offsetY
            })
            this.backgroundLayer.add(hLine)
          }
          // Add some random spots for texture
          for (let i = 0; i < 20; i++) {
            const circle = new Konva.Circle({
              x: Math.random() * patternWidth,
              y: Math.random() * patternHeight,
              radius: Math.random() * 30 + 10,
              fill: `rgba(0,0,0,${Math.random() * 0.02 + 0.01})`,
              offsetX: offsetX,
              offsetY: offsetY
            })
            this.backgroundLayer.add(circle)
          }
          break

        case 'parquet': {
          // Create realistic parquet pattern
          const patternCanvas = document.createElement('canvas')
          const patternSize = 300
          patternCanvas.width = patternSize
          patternCanvas.height = patternSize
          const ctx = patternCanvas.getContext('2d')

          const plankHeight = 15
          const plankWidths = [60, 80, 100, 70, 90] // Different plank widths
          const colors = [
            '#A67C52', // Light tan
            '#8B6F47', // Medium brown
            '#9C7E56', // Tan brown
            '#7D6344', // Dark tan
            '#B8956A', // Light brown
            '#8E7555' // Medium tan
          ]

          // Draw horizontal planks with varied widths
          let y = 0
          let rowIndex = 0
          while (y < patternSize + plankHeight) {
            let x = (rowIndex % 2) * -30 // Offset every other row
            while (x < patternSize + 100) {
              const plankWidth = plankWidths[Math.floor(Math.random() * plankWidths.length)]
              const baseColor = colors[Math.floor(Math.random() * colors.length)]

              // Draw plank
              ctx.fillStyle = baseColor
              ctx.fillRect(x, y, plankWidth, plankHeight)

              // Add subtle wood grain texture
              for (let i = 0; i < 3; i++) {
                ctx.strokeStyle = `rgba(0, 0, 0, ${0.03 + Math.random() * 0.02})`
                ctx.lineWidth = 0.5
                ctx.beginPath()
                const grainY = y + Math.random() * plankHeight
                ctx.moveTo(x, grainY)
                ctx.lineTo(x + plankWidth, grainY)
                ctx.stroke()
              }

              // Subtle edge shadow
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)'
              ctx.lineWidth = 0.5
              ctx.beginPath()
              ctx.moveTo(x, y)
              ctx.lineTo(x, y + plankHeight)
              ctx.stroke()

              x += plankWidth
            }
            y += plankHeight
            rowIndex++
          }

          // Use pattern as fillPatternImage
          const rect = new Konva.Rect({
            x: -offsetX,
            y: -offsetY,
            width: patternWidth,
            height: patternHeight,
            fillPatternImage: patternCanvas,
            fillPatternRepeat: 'repeat',
            fillPatternScale: { x: 1, y: 1 },
            opacity: 0.3
          })
          this.backgroundLayer.add(rect)
          break
        }
      }
    },

    /**
     * Draw pattern overlay for area
     */
    drawAreaPattern (group, area) {
      // Use relative coordinates (0, 0) since group already has position
      const x = 0
      const y = 0
      const width = area.width
      const height = area.height
      const patternOpacity = area.opacity * 0.3 // Make pattern more subtle

      switch (area.pattern) {
        case 'wood': {
          // Create vertical wood planks pattern
          const patternCanvas = document.createElement('canvas')
          const patternSize = 400
          patternCanvas.width = patternSize
          patternCanvas.height = patternSize
          const ctx = patternCanvas.getContext('2d')

          const plankWidth = 50
          const colors = [
            '#8B7355', // Medium brown
            '#A0826D', // Light brown
            '#7D6752', // Dark brown
            '#96795E', // Tan brown
            '#8A7456', // Medium tan
            '#7A6A52' // Dark tan
          ]

          // Draw vertical planks
          let px = 0
          while (px < patternSize) {
            const baseColor = colors[Math.floor(Math.random() * colors.length)]

            // Draw plank
            ctx.fillStyle = baseColor
            ctx.fillRect(px, 0, plankWidth, patternSize)

            // Add vertical wood grain texture
            for (let i = 0; i < 8; i++) {
              ctx.strokeStyle = `rgba(0, 0, 0, ${0.05 + Math.random() * 0.03})`
              ctx.lineWidth = 0.5 + Math.random() * 0.5
              ctx.beginPath()
              const grainX = px + Math.random() * plankWidth
              ctx.moveTo(grainX, 0)
              ctx.lineTo(grainX, patternSize)
              ctx.stroke()
            }

            // Dark edge between planks
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)'
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.moveTo(px, 0)
            ctx.lineTo(px, patternSize)
            ctx.stroke()

            px += plankWidth
          }

          // Use pattern as fillPatternImage
          const rect = new Konva.Rect({
            x,
            y,
            width,
            height,
            fillPatternImage: patternCanvas,
            fillPatternRepeat: 'repeat',
            fillPatternScale: { x: 1, y: 1 },
            opacity: patternOpacity * 0.8
          })
          group.add(rect)
          break
        }

        case 'tile': {
          // Checkerboard pattern
          const tileSize = 40
          for (let py = y; py < y + height; py += tileSize) {
            for (let px = x; px < x + width; px += tileSize) {
              if ((Math.floor((px - x) / tileSize) + Math.floor((py - y) / tileSize)) % 2 === 0) {
                const tile = new Konva.Rect({
                  x: px,
                  y: py,
                  width: tileSize,
                  height: tileSize,
                  fill: 'rgba(0,0,0,0.03)',
                  opacity: patternOpacity
                })
                group.add(tile)
              }
            }
          }
          break
        }

        case 'carpet': {
          // Diagonal lines pattern
          for (let i = -(height); i < width + height; i += 20) {
            const line1 = new Konva.Line({
              points: [x + i, y, x + i + height, y + height],
              stroke: 'rgba(139,0,0,0.05)',
              strokeWidth: 10,
              opacity: patternOpacity
            })
            group.add(line1)

            const line2 = new Konva.Line({
              points: [x + i, y, x + i - height, y + height],
              stroke: 'rgba(139,0,0,0.03)',
              strokeWidth: 10,
              opacity: patternOpacity
            })
            group.add(line2)
          }
          break
        }

        case 'marble': {
          // Marble effect with circles
          const marbleSpots = [
            { x: x + width * 0.2, y: y + height * 0.5, radius: Math.min(width, height) * 0.2, opacity: 0.15 },
            { x: x + width * 0.8, y: y + height * 0.8, radius: Math.min(width, height) * 0.15, opacity: 0.1 },
            { x: x + width * 0.4, y: y + height * 0.2, radius: Math.min(width, height) * 0.18, opacity: 0.12 },
            { x: x + width * 0.6, y: y + height * 0.6, radius: Math.min(width, height) * 0.16, opacity: 0.08 }
          ]
          marbleSpots.forEach(spot => {
            const circle = new Konva.Circle({
              x: spot.x,
              y: spot.y,
              radius: spot.radius,
              fill: `rgba(200,200,200,${spot.opacity})`,
              opacity: patternOpacity
            })
            group.add(circle)
          })
          break
        }

        case 'concrete': {
          // Grid pattern
          for (let i = x; i < x + width; i += 50) {
            const vLine = new Konva.Line({
              points: [i, y, i, y + height],
              stroke: 'rgba(0,0,0,0.03)',
              strokeWidth: 1,
              opacity: patternOpacity
            })
            group.add(vLine)
          }
          for (let i = y; i < y + height; i += 50) {
            const hLine = new Konva.Line({
              points: [x, i, x + width, i],
              stroke: 'rgba(0,0,0,0.03)',
              strokeWidth: 1,
              opacity: patternOpacity
            })
            group.add(hLine)
          }
          // Add some random spots for texture
          const spotCount = Math.floor((width * height) / 10000) // Scale with area size
          for (let i = 0; i < spotCount; i++) {
            const circle = new Konva.Circle({
              x: x + Math.random() * width,
              y: y + Math.random() * height,
              radius: Math.random() * 30 + 10,
              fill: `rgba(0,0,0,${Math.random() * 0.02 + 0.01})`,
              opacity: patternOpacity
            })
            group.add(circle)
          }
          break
        }

        case 'parquet': {
          // Create realistic parquet pattern
          const patternCanvas = document.createElement('canvas')
          const patternSize = 300
          patternCanvas.width = patternSize
          patternCanvas.height = patternSize
          const ctx = patternCanvas.getContext('2d')

          const plankHeight = 15
          const plankWidths = [60, 80, 100, 70, 90] // Different plank widths
          const colors = [
            '#A67C52', // Light tan
            '#8B6F47', // Medium brown
            '#9C7E56', // Tan brown
            '#7D6344', // Dark tan
            '#B8956A', // Light brown
            '#8E7555' // Medium tan
          ]

          // Draw horizontal planks with varied widths
          let py = 0
          let rowIndex = 0
          while (py < patternSize + plankHeight) {
            let px = (rowIndex % 2) * -30 // Offset every other row
            while (px < patternSize + 100) {
              const plankWidth = plankWidths[Math.floor(Math.random() * plankWidths.length)]
              const baseColor = colors[Math.floor(Math.random() * colors.length)]

              // Draw plank
              ctx.fillStyle = baseColor
              ctx.fillRect(px, py, plankWidth, plankHeight)

              // Add subtle wood grain texture
              for (let i = 0; i < 3; i++) {
                ctx.strokeStyle = `rgba(0, 0, 0, ${0.03 + Math.random() * 0.02})`
                ctx.lineWidth = 0.5
                ctx.beginPath()
                const grainY = py + Math.random() * plankHeight
                ctx.moveTo(px, grainY)
                ctx.lineTo(px + plankWidth, grainY)
                ctx.stroke()
              }

              // Subtle edge shadow
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)'
              ctx.lineWidth = 0.5
              ctx.beginPath()
              ctx.moveTo(px, py)
              ctx.lineTo(px, py + plankHeight)
              ctx.stroke()

              px += plankWidth
            }
            py += plankHeight
            rowIndex++
          }

          // Use pattern as fillPatternImage
          const rect = new Konva.Rect({
            x,
            y,
            width,
            height,
            fillPatternImage: patternCanvas,
            fillPatternRepeat: 'repeat',
            fillPatternScale: { x: 1, y: 1 },
            opacity: patternOpacity * 0.8
          })
          group.add(rect)
          break
        }
      }
    }
  }
}
</script>

<style scoped>
.floor-plan-canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background-color: #fafafa;
}

.canvas-wrapper {
  width: 100%;
  height: 100%;
}

.table-tooltip {
  max-width: 250px;
}
</style>
